import React, { useEffect, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SensorsIcon from '@mui/icons-material/Sensors';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Divider,
  IconButton,
  IconButtonProps,
  Stack,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import moment from 'moment/moment';

import { DisplayValue } from '../shared-ui';
import {
  DeviceAlerts,
  ToggleAlertDetailsButton,
} from '../shared-ui/alerts-info';
import { DeviceInfo, DeviceInformation } from '../shared-ui/device-info';
import DeviceLoadingSkeleton from '../shared-ui/deviceLoadingSkeleton';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';
import {
  useDeviceAndSourceAreOnline,
  useDeviceHasAlerts,
} from '../../../system/AlertsManager';
import { Notifier } from '../../../system/services/notificationManager';
import {
  ConductorGenericSensor,
  useConductorGenericSensorDetailQuery,
  useConductorGenericSensorDetailUpdateSubscription,
  useResetConductorGenericSensorCountersMutation,
} from '../../../../types/generated-types';

type MetricValue = {
  timestamp?: number | null;
  value?: boolean | number | string | null;
  units?: string | null;
  pending?: boolean | null;
};

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

function hasMetric(
  device: Partial<ConductorGenericSensor> | undefined,
  metricName: string,
) {
  return Object.prototype.hasOwnProperty.call(
    device?.meta?.metrics ?? {},
    metricName,
  );
}

export function ConductorGenericSensorComponent({
  deviceId,
}: {
  deviceId: string;
}) {
  const [device, setDevice] = useState<Partial<ConductorGenericSensor>>();
  const [deviceInfo, setDeviceInfo] = useState<DeviceInformation[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [alertsExpanded, setAlertsExpanded] = useState(false);
  const { data, loading, error } = useConductorGenericSensorDetailQuery({
    variables: { id: deviceId },
  });

  useConductorGenericSensorDetailUpdateSubscription({
    variables: { ids: [deviceId] },
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  const [resetCounters, { loading: resetLoading }] =
    useResetConductorGenericSensorCountersMutation({
      variables: { deviceId },
    });

  const handleResetCounters = async () => {
    try {
      await resetCounters();
      Notifier.success('Conductor sensor counters reset.');
    } catch {
      Notifier.error('Unable to reset Conductor sensor counters.');
    }
  };

  useEffect(() => {
    if (data?.conductorGenericSensorById) {
      setDevice(
        data.conductorGenericSensorById as Partial<ConductorGenericSensor>,
      );
    }
  }, [data]);

  const hasAlerts = useDeviceHasAlerts(device);
  const isOnline = useDeviceAndSourceAreOnline(device);

  useEffect(() => {
    if (!device) {
      setDeviceInfo([]);
      return;
    }

    const locationInfo: DeviceInformation = {
      Zone: device.zone?.name ?? 'Unnamed',
      'Zone ID': device.zoneId ?? 'Unidentified',
      Source: device.source?.name ?? 'Unnamed source',
    };

    const baseDeviceInfo: DeviceInformation = {
      'Device ID': device.deviceId ?? device._id ?? 'Unidentified',
      Name: device.name ?? device.displayName ?? 'Unnamed',
      Type: device.typeDisplayName ?? 'Unknown',
      firmwareInfo: {
        Model:
          device.meta?.model || device.meta?.modelNumber || 'Unknown model',
        Version: device.meta?.version || 'Unknown version',
      },
      'Last Updated': device.timestamp
        ? moment(device.timestamp).fromNow()
        : 'Unknown',
    };

    setDeviceInfo([locationInfo, baseDeviceInfo]);
  }, [device]);

  if (loading && !device) {
    return <DeviceLoadingSkeleton />;
  }

  if (error) {
    return <Alert severity="error">Unable to load Conductor sensor.</Alert>;
  }

  if (!device) {
    return <Alert severity="warning">Conductor sensor not found.</Alert>;
  }

  const metricCards = [
    hasMetric(device, 'temperature')
      ? {
          label: 'Temperature',
          metric: device.temperature,
          digits: 1,
        }
      : undefined,
    hasMetric(device, 'humidity')
      ? {
          label: 'Humidity',
          metric: device.humidity,
          digits: 0,
        }
      : undefined,
    hasMetric(device, 'setpoint')
      ? {
          label: 'Setpoint',
          metric: device.setpoint,
          digits: 1,
        }
      : undefined,
    hasMetric(device, 'systemMode')
      ? {
          label: 'Mode',
          metric: device.systemMode,
          digits: 0,
        }
      : undefined,
    hasMetric(device, 'fanSpeed')
      ? {
          label: 'Fan',
          metric: device.fanSpeed,
          digits: 0,
        }
      : undefined,
    hasMetric(device, 'alarmState')
      ? {
          label: 'Alarm',
          metric: device.alarmState,
          digits: 0,
        }
      : undefined,
    hasMetric(device, 'counterValue')
      ? {
          label: 'Counter',
          metric: device.counterValue,
          digits: 0,
        }
      : undefined,
    hasMetric(device, 'resetCount')
      ? {
          label: 'Resets',
          metric: device.resetCount,
          digits: 0,
        }
      : undefined,
  ].filter(Boolean) as Array<{
    label: string;
    metric?: MetricValue | null;
    digits: number;
  }>;

  const displayName = device.displayName ?? device.name ?? 'Conductor Sensor';
  const headerStatusText = isOnline ? displayName : `${displayName}: Offline`;

  return (
    <Card
      sx={{
        border: hasAlerts ? '2px solid red' : '1px solid lightgray',
        borderRadius: '4px',
        boxShadow: 2,
        p: 0,
        width: '100%',
      }}
    >
      <CardHeader
        sx={{
          backgroundColor: '#5eb85ead',
          borderBottom: '1px solid lightgray',
          padding: '4px',
          '& .MuiCardHeader-content': {
            minWidth: 0,
          },
          '& .MuiCardHeader-title': {
            display: 'block',
            minWidth: 0,
            width: '100%',
          },
          '& .MuiCardHeader-action': {
            alignSelf: 'center',
            margin: 0,
          },
        }}
        avatar={
          <Avatar
            sx={{
              backgroundColor: 'transparent',
              height: '25px',
              padding: '0px',
              width: '25px',
            }}
          >
            <SensorsIcon
              sx={{
                color: '#1E3D1D',
                margin: '0',
              }}
            />
          </Avatar>
        }
        title={
          <Typography
            noWrap
            variant="h4"
            sx={{
              color: isOnline ? '#1E3D1D' : 'red',
              marginLeft: '10px',
            }}
          >
            {headerStatusText}
          </Typography>
        }
        action={
          <Box sx={{ display: 'flex', margin: 0, padding: 0 }}>
            <ToggleAlertDetailsButton
              buttonLabel="Show alerts"
              device={device}
              expand={alertsExpanded}
              showDivider={true}
              onClick={(event) => {
                event.preventDefault();
                setAlertsExpanded(!alertsExpanded);
              }}
            />
            <ExpandMore
              aria-expanded={expanded}
              aria-label="show more"
              expand={expanded}
              onClick={() => setExpanded(!expanded)}
            >
              <ExpandMoreIcon />
            </ExpandMore>
          </Box>
        }
      />
      <CardContent
        sx={{
          px: 0,
          py: 0,
          '&.MuiCardContent-root:last-child': { pb: 0 },
        }}
      >
        <Box sx={{ px: 0, py: 0 }}>
          <Stack
            alignItems="center"
            direction="row"
            divider={<Divider orientation="vertical" flexItem />}
            justifyContent="space-evenly"
            sx={{ py: 1 }}
          >
            {metricCards.map(({ label, metric, digits }) =>
              typeof metric?.value === 'boolean' ? (
                <Stack
                  key={label}
                  alignItems="center"
                  justifyContent="center"
                  spacing={0.5}
                  style={{ minWidth: 86 }}
                >
                  <Typography variant="h2">
                    {metric.value ? 'Active' : 'Normal'}
                  </Typography>
                  <Typography
                    noWrap
                    align="center"
                    variant="caption"
                    sx={{ marginTop: '0px !important', fontSize: 'xx-small' }}
                  >
                    {label.toUpperCase()}
                  </Typography>
                </Stack>
              ) : (
                <DisplayValue
                  key={label}
                  value={metric?.value}
                  units={metric?.units}
                  digits={digits}
                  label={label}
                  size="h2"
                  unitsSize="22px"
                  style={{ minWidth: 86 }}
                />
              ),
            )}
          </Stack>
          {device.supportsResetCounters ? (
            <Box sx={{ px: 1, pb: 1 }}>
              <Button
                fullWidth
                startIcon={<RestartAltIcon />}
                disabled={resetLoading}
                onClick={handleResetCounters}
                size="small"
                variant="outlined"
              >
                Reset Counters
              </Button>
            </Box>
          ) : null}
        </Box>
      </CardContent>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box
          sx={{
            '& .pair .left': {
              flex: '0 0 82px',
              minWidth: 0,
            },
            '& .pair .right': {
              flex: '1 1 auto',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
          }}
        >
          <DeviceInfo infoEntries={deviceInfo} />
        </Box>
      </Collapse>
      <DeviceAlerts device={device} alertsExpanded={alertsExpanded} />
    </Card>
  );
}
